import React, { createContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import jwt_decode from "jwt-decode";
import { isPrerendering } from "../utils/prerender";

const AuthStateContext = createContext({});

function AuthStateProvider({ children }) {
  const suppressAuthRedirectRef = useRef(false);
  const [avatarVersion, setAvatarVersion] = useState(Date.now());

  const [user, setUser] = useState(() => {
    // Skip localStorage during prerendering
    if (isPrerendering() || typeof window === 'undefined') {
      return null;
    }
    
    const access = localStorage.getItem("access_token");
    if (access) {
      try {
        return jwt_decode(access);
      } catch (error) {
        console.error("Failed to decode access token:", error);
        return null;
      }
    }
    return null;
  });

  const [userDetails, setUserDetails] = useState(() => {
    // Skip sessionStorage during prerendering
    if (isPrerendering() || typeof window === 'undefined') {
      return null;
    }
    
    const saved = window.sessionStorage.getItem('userDetails');
    return saved ? JSON.parse(saved) : null;
  });

  const [authRedirectPath, setAuthRedirectPath] = useState(null);
  const authRedirectPathRef = useRef(null);
  useEffect(() => {
    authRedirectPathRef.current = authRedirectPath;
  }, [authRedirectPath]);

  const [accessToken, setAccessToken] = useState(() => {
    return localStorage.getItem("access_token");
  });

  const accessTokenRef = useRef(accessToken); 
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const [email, setEmail] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const pendingRequests = useRef([]);

  const processPendingRequests = (token) => {
    pendingRequests.current.forEach((request) => {
      request.config.headers["Authorization"] = `Bearer ${token}`;
      request.resolve(request.config);
    });
    pendingRequests.current.length = 0;
  };

  const rejectPendingRequests = () => {
    pendingRequests.current.forEach((request) => {
      request.reject(new Error("User is not authenticated"));
    });
    pendingRequests.current.length = 0;
  };

  const storeUser = useCallback((response) => {
    // Handle new allauth headless API format
    let accessToken, refreshToken, sessionToken;
    
    if (response.meta) {
      // New allauth format: tokens in meta, user data in both data.user and meta.user
      accessToken = response.meta.access_token;
      refreshToken = response.meta.refresh_token;
      sessionToken = response.meta.session_token;
    } else {
      // Fallback for old format
      accessToken = response.access || response.access_token;
      refreshToken = response.refresh || response.refresh_token;
    }
    
    if (!accessToken || !refreshToken) {
      return;
    }
    
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    
    // Store session token if available (new allauth format)
    if (sessionToken) {
      localStorage.setItem("session_token", sessionToken);
    }
    
    setUser(jwt_decode(accessToken));
    setAccessToken(accessToken);
    accessTokenRef.current = accessToken;
    processPendingRequests(accessToken);
    
    // redirect happens automatically in auth-redirector
  }, []);

  const clearUser = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("session_token");
    sessionStorage.removeItem("userDetails");
    setUser(null);
    setUserDetails(null);
    setAccessToken(null);
    rejectPendingRequests();
  }, []);
  
  useEffect(() => {
    if (accessToken) {
      try {
        const decodedToken = jwt_decode(accessToken);
        const currentTime = Date.now() / 1000;
        const timeLeft = decodedToken.exp - currentTime;
    
        if (timeLeft <= 0) {
          clearUser();
        } else {
          const bufferTime = 30;
          const adjustedTimeLeft = timeLeft - bufferTime > 0 ? (timeLeft - bufferTime) * 1000 : 0;
    
          const timeoutId = setTimeout(() => {
            clearUser();
          }, adjustedTimeLeft);
    
          return () => clearTimeout(timeoutId);
        }
      } catch (error) {
        console.error("Failed to decode access token:", error);
        clearUser();
      }
    }
  }, [accessToken, clearUser]);

  const value = useMemo(() => ({
    user,
    setUser,
    userDetails,
    setUserDetails,
    storeUser,
    clearUser,
    email,
    setEmail,
    sessionToken,
    setSessionToken,
    accessToken,
    setAccessToken,
    accessTokenRef,
    pendingRequests,
    authRedirectPath,
    setAuthRedirectPath,
    authRedirectPathRef,
    suppressAuthRedirectRef,
    avatarVersion,
    setAvatarVersion
  }), [user, userDetails, storeUser, clearUser, email, sessionToken, accessToken, authRedirectPath, avatarVersion]);

  return (
    <AuthStateContext.Provider value={value}>
      {children}
    </AuthStateContext.Provider>
  );
}

export { AuthStateProvider, AuthStateContext };